import type { ChangeEventHandler, InputHTMLAttributes, JSX } from "react";

import { strings } from "./strings.js";
import type { Capabilities } from "./wire.js";

const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const DISPLAY_NAME_STORAGE_KEY = "klass-share.display-name.v1";

export type LnaBrowserFamily =
  | "chrome"
  | "edge"
  | "generic"
  | "samsung"
  | "webos";

interface LnaGuidance {
  readonly label: string;
  readonly steps: readonly string[];
}

const LNA_GUIDANCE: Record<LnaBrowserFamily, LnaGuidance> =
  strings.lna.browsers;

export function initialJoinDisplayName(
  storage: Pick<Storage, "getItem">,
): string {
  return storage.getItem(DISPLAY_NAME_STORAGE_KEY) ?? "";
}

export function shouldUseSingleInputFallback(
  capabilities: Pick<Capabilities, "deviceClass" | "userAgent">,
): boolean {
  return (
    capabilities.deviceClass === "webos" ||
    /SamsungBrowser\//iu.test(capabilities.userAgent)
  );
}

export function classifyLnaBrowser(
  capabilities: Pick<Capabilities, "deviceClass" | "userAgent">,
): LnaBrowserFamily {
  if (capabilities.deviceClass === "webos") return "webos";
  if (/SamsungBrowser\//iu.test(capabilities.userAgent)) return "samsung";
  if (/Edg\//iu.test(capabilities.userAgent)) return "edge";
  if (/(?:Chrome|CriOS)\//iu.test(capabilities.userAgent)) return "chrome";
  return "generic";
}

export interface SegmentedInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "maxLength" | "onChange" | "type" | "value"
> {
  readonly fallback?: boolean;
  readonly length: number;
  readonly mode: "code";
  readonly onChange: ChangeEventHandler<HTMLInputElement>;
  readonly value: string;
}

export function SegmentedInput({
  className,
  fallback = false,
  length,
  mode,
  onChange,
  value,
  ...inputProps
}: SegmentedInputProps): JSX.Element {
  const characters = Array.from(value).slice(0, length);
  const hasInvalidCharacter = characters.some((character) =>
    invalidSegmentCharacter(mode, character),
  );
  const currentIndex = Math.min(characters.length, length - 1);
  const ariaInvalid =
    inputProps["aria-invalid"] === undefined && hasInvalidCharacter
      ? true
      : inputProps["aria-invalid"];

  return (
    <div
      className={`segmented-input${fallback ? " segmented-input-fallback" : ""}`}
      data-length={length}
      data-mode={mode}
      data-segmented-for={inputProps.id}
    >
      <div className="segment-cells" aria-hidden="true">
        {Array.from({ length }, (_, index) => {
          const character = characters[index] ?? "";
          const invalid = invalidSegmentCharacter(mode, character);
          return (
            <span
              key={index}
              className={`segment-cell${character === "" ? "" : " is-filled"}${index === currentIndex ? " is-current" : ""}${invalid ? " is-invalid" : ""}`}
              data-cell-index={index}
              data-invalid={invalid ? "true" : undefined}
            >
              {character}
            </span>
          );
        })}
      </div>
      <input
        {...inputProps}
        aria-invalid={ariaInvalid}
        className={`segmented-control${className === undefined ? "" : ` ${className}`}`}
        maxLength={length}
        onChange={onChange}
        type="text"
        value={value}
      />
    </div>
  );
}

export function LnaPromptCallout({
  capabilities,
  message,
}: {
  readonly capabilities: Pick<Capabilities, "deviceClass" | "userAgent">;
  readonly message: string;
}): JSX.Element {
  const browser = classifyLnaBrowser(capabilities);
  return (
    <div
      className="connection-banner lna-prompt-callout"
      data-browser={browser}
      role="status"
    >
      <span className="lna-callout-icon" aria-hidden="true">
        <InfoIcon />
      </span>
      <span>
        <strong>{strings.lna.promptTitle}</strong>
        <small>{message}</small>
      </span>
    </div>
  );
}

export function LnaRecoveryGuidance({
  capabilities,
}: {
  readonly capabilities: Pick<Capabilities, "deviceClass" | "userAgent">;
}): JSX.Element {
  const browser = classifyLnaBrowser(capabilities);
  const guidance = LNA_GUIDANCE[browser];
  return (
    <>
      <div
        className="lna-illustration-slot"
        data-browser={browser}
        aria-hidden="true"
      >
        <DevicesIcon />
      </div>
      <div className="lna-recovery-copy">
        <span className="eyebrow">{guidance.label}</span>
        <h2>{strings.lna.deniedTitle}</h2>
        <p>{strings.lna.deniedDetail}</p>
        <ol>
          {guidance.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
    </>
  );
}

export function ChangeBoardIcon(): JSX.Element {
  // KlassMate source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1ClassroomEditIcon.tsx.
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.9 1.3L12.7 3.1C13.1 3.5 13.1 4.1 12.7 4.5L5.2 12H2V8.8L9.5 1.3C9.9 0.9 10.5 0.9 10.9 1.3ZM12 3.8L10.2 2L8.7 3.5L10.5 5.3L12 3.8ZM3 9.2V11H4.8L9.8 6L8 4.2L3 9.2ZM1 14V13H15V14H1Z"
      />
    </svg>
  );
}

function invalidSegmentCharacter(
  mode: SegmentedInputProps["mode"],
  character: string,
): boolean {
  if (character === "") return false;
  return !CROCKFORD_ALPHABET.includes(character.toUpperCase());
}

function InfoIcon(): JSX.Element {
  // KlassMate source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1ClassroomInfoGreenIcon.tsx.
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 1.5C9.92333 1.5 7.89326 2.11582 6.16653 3.26958C4.43981 4.42334 3.094 6.06322 2.29927 7.98185C1.50455 9.90048 1.29661 12.0117 1.70176 14.0485C2.10691 16.0853 3.10694 17.9562 4.5754 19.4247C6.04385 20.8931 7.91478 21.8932 9.95159 22.2983C11.9884 22.7035 14.0996 22.4955 16.0182 21.7008C17.9369 20.9061 19.5767 19.5603 20.7305 17.8335C21.8843 16.1068 22.5001 14.0768 22.5001 12C22.5001 9.21526 21.3938 6.54453 19.4247 4.57539C17.4556 2.60625 14.7848 1.5 12 1.5ZM12 6.00002C12.2225 6.00002 12.4401 6.066 12.6251 6.18961C12.8101 6.31323 12.9543 6.48893 13.0394 6.6945C13.1246 6.90007 13.1468 7.12627 13.1034 7.3445C13.06 7.56273 12.9529 7.76319 12.7955 7.92052C12.6382 8.07785 12.4378 8.185 12.2195 8.22841C12.0013 8.27182 11.7751 8.24954 11.5695 8.16439C11.364 8.07924 11.1883 7.93505 11.0646 7.75004C10.941 7.56503 10.875 7.34753 10.875 7.12502C10.875 6.82665 10.9936 6.5405 11.2045 6.32952C11.4155 6.11854 11.7017 6.00002 12 6.00002ZM15.0001 18.0938H9.00003V16.4063H11.1563V12.0938H9.75004V10.4063H12.8438V16.4063H15.0001V18.0938Z"
        fill="currentColor"
      />
    </svg>
  );
}

function DevicesIcon(): JSX.Element {
  // KlassMate source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1WhiteboardDevicesMediumIcon.tsx.
  return (
    <svg
      viewBox="0 0 20 18"
      width="40"
      height="36"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.39855 1.89293C2.39855 1.54768 2.67843 1.26781 3.02367 1.26781H18.1253C18.4705 1.26781 18.7504 1.54768 18.7504 1.89293V10.8228C18.7504 11.1681 18.4705 11.4479 18.1253 11.4479H9.12315V12.6982H10.7724L10.8034 16.7319H8.74219V17.9821H15.0286V16.7319H12.0536L12.0226 12.6982H18.1253C19.161 12.6982 20.0006 11.8585 20.0006 10.8228V1.89293C20.0006 0.857201 19.161 0.0175781 18.1253 0.0175781H3.02367C1.98794 0.0175781 1.14832 0.8572 1.14832 1.89293V4.72496H2.39855V1.89293ZM1.25023 16.5837V7.65381L6.29352 7.65381L6.29352 16.5837H1.25023ZM0 16.5837V7.65381C0 6.96333 0.559747 6.40358 1.25023 6.40358H6.29352C6.98401 6.40358 7.54376 6.96333 7.54376 7.65381V16.5837C7.54376 17.2742 6.98401 17.8339 6.29352 17.8339H1.25023C0.559748 17.8339 0 17.2742 0 16.5837Z"
        fill="currentColor"
      />
    </svg>
  );
}
